import { useEffect } from 'react';
import { RxCross2 } from 'react-icons/rx';

interface AddressDiff {
    field: string;
    label: string;
    current: string;
    mip: string;
}

interface AddressOverwriteConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isLoading?: boolean;
    vendorName?: string;
    vendorId?: string;
    differences: AddressDiff[];
    currentAddress: Record<string, string>;
    mipAddress: Record<string, string>;
}

const AddressOverwriteConfirmModal: React.FC<
    AddressOverwriteConfirmModalProps
> = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading = false,
    vendorName,
    vendorId,
    differences,
    currentAddress,
    mipAddress,
}) => {
    useEffect(() => {
        const keyHandler = ({ keyCode }: KeyboardEvent) => {
            if (keyCode === 27) onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', keyHandler);
        }
        return () => document.removeEventListener('keydown', keyHandler);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const fieldLabels: Record<string, string> = {
        address_1: 'Street Address 1',
        address_2: 'Street Address 2',
        city: 'City',
        state: 'State',
        zip_code: 'Zip Code',
    };
    const allFields = ['address_1', 'address_2', 'city', 'state', 'zip_code'];
    const diffFields = new Set(differences.map((d) => d.field));

    const hasMissingMipField = differences.some((d) => !d.mip && d.current);

    return (
        <div
            className="fixed left-0 top-0 z-999999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-2xl rounded-lg bg-white shadow-lg"
            >
                <button
                    className="absolute right-4 top-4 text-gray-600 hover:text-gray-900"
                    onClick={onClose}
                    disabled={isLoading}
                >
                    <RxCross2 />
                </button>
                <div className="p-8">
                    <h2 className="mb-2 text-[22px] font-bold">
                        Address Differences Detected
                    </h2>
                    <p className="mb-6 text-gray-600">
                        The address in MIP differs from the current address on
                        record
                        {vendorName && (
                            <>
                                {' '}
                                for <strong>{vendorName}</strong>
                            </>
                        )}
                        {vendorId && (
                            <>
                                {' '}
                                ({vendorId})
                            </>
                        )}
                        . Verifying will overwrite the current address with the
                        MIP data shown below.
                    </p>

                    {hasMissingMipField && (
                        <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                            One or more address fields are empty in MIP. Proceeding will
                            clear those fields on the current record.
                        </div>
                    )}

                    <div className="mb-6 overflow-hidden rounded border border-gray-200">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 text-left">
                                    <th className="px-4 py-3 font-semibold text-gray-600">
                                        Field
                                    </th>
                                    <th className="px-4 py-3 font-semibold text-gray-600">
                                        Current (Vendor)
                                    </th>
                                    <th className="px-4 py-3 font-semibold text-gray-600">
                                        MIP
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {allFields.map((field) => {
                                    const isDiff = diffFields.has(field);
                                    const currentVal =
                                        currentAddress[field] || '';
                                    const mipVal = mipAddress[field] || '';
                                    const mipIsEmpty = isDiff && !mipVal && !!currentVal;

                                    return (
                                        <tr
                                            key={field}
                                            className={
                                                isDiff
                                                    ? 'bg-amber-50'
                                                    : ''
                                            }
                                        >
                                            <td className="border-t px-4 py-2 font-medium text-gray-700">
                                                {fieldLabels[field]}
                                            </td>
                                            <td
                                                className={`border-t px-4 py-2 ${
                                                    isDiff
                                                        ? 'text-red-700 line-through'
                                                        : 'text-gray-600'
                                                }`}
                                            >
                                                {currentVal || (
                                                    <span className="italic text-gray-400">
                                                        empty
                                                    </span>
                                                )}
                                            </td>
                                            <td
                                                className={`border-t px-4 py-2 ${
                                                    isDiff
                                                        ? mipIsEmpty
                                                            ? 'font-semibold text-amber-700'
                                                            : 'font-semibold text-green-700'
                                                        : 'text-gray-600'
                                                }`}
                                            >
                                                {mipVal || (
                                                    <span className={`italic ${mipIsEmpty ? 'text-amber-600' : 'text-gray-400'}`}>
                                                        empty
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-start space-x-4">
                        <button
                            onClick={onClose}
                            disabled={isLoading}
                            className="rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={isLoading}
                            className="rounded-full bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:bg-yellow disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {isLoading
                                ? 'Overwriting...'
                                : 'Overwrite with MIP'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddressOverwriteConfirmModal;
